import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCheck, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { maskCPF, maskRG, maskPhone, maskCEP, onlyDigits, fetchCEP } from "@/lib/masks";

export default function CompleteProfile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nome: user?.name || "",
    cpf: "", rg: "", data_nasc: "", sexo: "", telefone: "",
    endereco: { logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", cep: "" },
    registro_conselho: "", formacao: "",
    dados_bancarios: { pix_tipo: "", pix_chave: "", banco: "", agencia: "", conta: "", tipo_conta: "" },
    nova_senha: "", confirma_senha: "",
  });

  const isTerapeuta = user?.role === "terapeuta";

  const validate = () => {
    const required = ["nome", "cpf", "data_nasc", "telefone"];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === "") {
        toast.error(`Campo obrigatório: ${k}`);
        return false;
      }
    }
    const end = form.endereco;
    if (!end.logradouro || !end.numero || !end.cidade || !end.estado || !end.cep) {
      toast.error("Preencha o endereço completo (logradouro, número, cidade, UF, CEP)");
      return false;
    }
    if (isTerapeuta && !form.registro_conselho) {
      toast.error("Terapeutas devem informar o registro do conselho (CRP, CREFITO etc.)");
      return false;
    }
    const db = form.dados_bancarios;
    if (!db.pix_tipo || !db.pix_chave) {
      toast.error("Cadastre uma chave PIX para receber pagamentos");
      return false;
    }
    if (form.nova_senha) {
      if (form.nova_senha.length < 6) { toast.error("A nova senha deve ter ao menos 6 caracteres"); return false; }
      if (form.nova_senha !== form.confirma_senha) { toast.error("A confirmação de senha não confere"); return false; }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.confirma_senha;
      if (!payload.nova_senha) delete payload.nova_senha;
      await api.post("/funcionarios/complete-profile", payload);
      toast.success("Perfil completo! Bem-vindo(a)");
      if (refresh) await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FBFC] via-white to-[#FBE1EC] py-8" data-testid="complete-profile-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-brand-turquoise text-white mb-3 shadow-lg">
            <UserCheck className="h-8 w-8" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-brand-turquoise">Bem-vindo(a) ao Anjos de Patas!</h1>
          <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
            Antes de continuar, complete seu cadastro. Estes dados são necessários para folha de pagamento e emissão de documentos.
          </p>
        </div>

        <Card className="border-border shadow-lg">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Section title="Dados pessoais">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Nome completo" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cp-nome" /></F>
                  <F label="CPF" req><Input required value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} placeholder="000.000.000-00" data-testid="cp-cpf" /></F>
                  <F label="RG"><Input value={form.rg} onChange={(e) => setForm({ ...form, rg: maskRG(e.target.value) })} placeholder="00.000.000-0" /></F>
                  <F label="Data de nascimento" req><Input required type="date" value={form.data_nasc} onChange={(e) => setForm({ ...form, data_nasc: e.target.value })} data-testid="cp-datanasc" /></F>
                  <F label="Sexo">
                    <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="feminino">Feminino</SelectItem>
                        <SelectItem value="masculino">Masculino</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Telefone" req><Input required value={form.telefone} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" data-testid="cp-tel" /></F>
                </div>
              </Section>

              <Section title="Endereço completo">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <div className="md:col-span-2"><F label="CEP" req><Input required placeholder="00000-000" value={form.endereco.cep} onChange={async (e) => {
                    const masked = maskCEP(e.target.value);
                    setForm((f) => ({ ...f, endereco: { ...f.endereco, cep: masked } }));
                    if (onlyDigits(masked).length === 8) {
                      const r = await fetchCEP(masked);
                      if (r) setForm((f) => ({ ...f, endereco: { ...f.endereco, logradouro: r.logradouro || f.endereco.logradouro, bairro: r.bairro || f.endereco.bairro, cidade: r.cidade || f.endereco.cidade, estado: r.estado || f.endereco.estado } }));
                    }
                  }} /></F></div>
                  <div className="md:col-span-3"><F label="Logradouro" req><Input required value={form.endereco.logradouro} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, logradouro: e.target.value } })} /></F></div>
                  <div><F label="Nº" req><Input required value={form.endereco.numero} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, numero: e.target.value } })} /></F></div>
                  <div className="md:col-span-2"><F label="Complemento"><Input value={form.endereco.complemento} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, complemento: e.target.value } })} /></F></div>
                  <div className="md:col-span-2"><F label="Bairro"><Input value={form.endereco.bairro} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, bairro: e.target.value } })} /></F></div>
                  <div className="md:col-span-1"><F label="Cidade" req><Input required value={form.endereco.cidade} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cidade: e.target.value } })} /></F></div>
                  <div><F label="UF" req><Input required maxLength={2} value={form.endereco.estado} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, estado: e.target.value.toUpperCase() } })} /></F></div>
                </div>
              </Section>

              <Section title="Formação profissional">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label={`Registro do conselho${isTerapeuta ? " *" : ""}`}>
                    <Input value={form.registro_conselho} onChange={(e) => setForm({ ...form, registro_conselho: e.target.value })} placeholder="Ex: CREFITO 12345 / CRP 06/12345" data-testid="cp-conselho" />
                  </F>
                  <F label="Formação"><Input value={form.formacao} onChange={(e) => setForm({ ...form, formacao: e.target.value })} placeholder="Ex: Bacharel em Fisioterapia" /></F>
                </div>
              </Section>

              <Section title="Dados bancários (para pagamento)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <F label="Tipo de PIX" req>
                    <Select value={form.dados_bancarios.pix_tipo} onValueChange={(v) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix_tipo: v } })}>
                      <SelectTrigger data-testid="cp-pixtipo"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                        <SelectItem value="telefone">Telefone</SelectItem>
                        <SelectItem value="aleatoria">Aleatória</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Chave PIX" req className="md:col-span-2"><Input required value={form.dados_bancarios.pix_chave} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix_chave: e.target.value } })} data-testid="cp-pixchave" /></F>
                  <F label="Banco"><Input value={form.dados_bancarios.banco} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, banco: e.target.value } })} /></F>
                  <F label="Agência"><Input value={form.dados_bancarios.agencia} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, agencia: e.target.value } })} /></F>
                  <F label="Conta"><Input value={form.dados_bancarios.conta} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, conta: e.target.value } })} /></F>
                </div>
              </Section>

              <Section title="Definir nova senha (opcional, mas recomendado)">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Nova senha"><Input type="password" value={form.nova_senha} onChange={(e) => setForm({ ...form, nova_senha: e.target.value })} placeholder="Deixe vazio para manter a temporária" data-testid="cp-senha" /></F>
                  <F label="Confirmar senha"><Input type="password" value={form.confirma_senha} onChange={(e) => setForm({ ...form, confirma_senha: e.target.value })} /></F>
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Sua senha é armazenada de forma criptografada.
                </p>
              </Section>

              <div className="pt-3 border-t border-border">
                <Button type="submit" disabled={saving} className="w-full bg-brand-turquoise hover:bg-[#3A9CA2] text-white h-11 text-base" data-testid="cp-submit">
                  <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando…" : "Salvar e acessar o sistema"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-3">{title}</h3>
      {children}
    </section>
  );
}

function F({ label, req, className, children }) {
  return (<div className={`space-y-1.5 ${className || ""}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
